/**
 * HHGlobal Design System Colors
 * System colors are hard-coded color values that cannot be changed.
 * System colors use a neutral color palette and support light and dark mode.
 */

/**
 * Neutral Colors
 * Applied frequently as background and text color treatments.
 */
export const neutralColors = {
  superblack: '#1A1A1A', // Primary text, headings, body content, form labels, buttons
  superblackPure: '#000000', // Pure black variant
  gray900: '#1A1A1A', // Button Strong - Primary, secondary and tertiary buttons
  gray700: '#4D4D4D', // Text Weak - Secondary text to make it less prominent
  gray600: '#767676', // Button Disabled - Disabled buttons
  gray500: '#888888', // Stroke Strong - Non-decorative borders, form input fields, icons
  gray400: '#CCCCCC', // Fill Weak - Fill for large components, file upload drop zones
  gray300: '#B3B3B3', // Stroke Weak - Decorative borders, dividing lines
  gray100: '#E6E6E6', // Fill Strong - Fill for tags, badges on base background
  gray50: '#F2F2F2', // Fill Weak - Fill for large components, file upload drop zones
} as const;

/**
 * Utility Colors
 * System colors indicate info, success, warnings, and error states.
 */

// Info Blue - Used to indicate a neutral message
export const infoBlue = {
  900: '#006699', // Text Strong - Primary text, body content, form labels
  800: '#0081BF', // Text Weak - Secondary text or iconography
  100: '#F0F9FF', // Fill - Alerts and badges on base background
} as const;

// Success Green - Used to indicate a positive message or completed action
export const successGreen = {
  900: '#00804A', // Text Strong - Primary text, body content, form labels
  800: '#00A85C', // Text Weak - Secondary text or iconography (estimated, not in image)
  100: '#E6F7F0', // Fill - Alerts and badges on base background (estimated)
} as const;

// Warning Yellow - Used to warn people to be cautious
export const warningYellow = {
  900: '#955904', // Text Strong - Primary text, body content, form labels
  800: '#F89406', // Text Weak - Secondary text or iconography
  100: '#FEF4E6', // Fill - Alerts and badges on base background
} as const;

// Error Red - Used to indicate a negative message, error, or system failure
export const errorRed = {
  900: '#8B0000', // Text Strong - Primary text, body content, form labels (estimated)
  800: '#DC2626', // Text Weak - Secondary text or iconography (estimated)
  100: '#FEE2E2', // Fill - Alerts and badges on base background (estimated)
} as const;

/**
 * Brand Colors
 * Applied to specific elements to enhance the look and feel of the system.
 * Brand colors should only be used as accent colors.
 */

// Primary Brand Colors
export const primaryBrand = {
  superblack: '#000000',
  impactGreen: '#AEFF24',
  emeraldGreen: '#00E686',
} as const;

// Secondary Brand Colors
export const secondaryBrand = {
  skyBlue: '#0099E5',
  deepBlue: '#006599',
  hotPink: '#F3318A',
  popViolet: '#702BE8',
  sunYellow: '#FFD600',
  pumpkinOrange: '#F27728',
} as const;

/**
 * Color Purpose Mapping
 * Maps semantic purposes to specific colors for easy reference
 */
export const colorPurpose = {
  // Text
  textStrong: neutralColors.superblack,
  textWeak: neutralColors.gray700,

  // Buttons
  buttonStrong: neutralColors.gray900,

  // Strokes/Borders
  strokeStrong: neutralColors.gray500,
  strokeWeak: neutralColors.gray300,

  // Fills
  fillStrong: neutralColors.gray100,
  fillWeak: neutralColors.gray50,

  // Utility States
  info: {
    text: infoBlue[900],
    textWeak: infoBlue[800],
    fill: infoBlue[100],
  },
  success: {
    text: successGreen[900],
    textWeak: successGreen[800],
    fill: successGreen[100],
  },
  warning: {
    text: warningYellow[900],
    textWeak: warningYellow[800],
    fill: warningYellow[100],
  },
  error: {
    text: errorRed[900],
    textWeak: errorRed[800],
    fill: errorRed[100],
  },
} as const;
